/**
 * Sync Status Badge
 *
 * Displays the sync status and last synced time for an Encompass template.
 * Includes a refresh button to trigger a new sync.
 */

import React from 'react';
import { RefreshCw, Check, AlertCircle, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';

interface SyncStatusBadgeProps {
  status: 'synced' | 'pending' | 'error';
  lastSyncedAt: string;
  syncError?: string | null;
  onSync?: () => void;
  isSyncing?: boolean;
}

export const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({
  status,
  lastSyncedAt,
  syncError,
  onSync,
  isSyncing = false
}) => {
  const getStatusIcon = () => {
    if (isSyncing) {
      return <Loader2 className="h-3 w-3 animate-spin" />;
    }
    switch (status) {
      case 'synced':
        return <Check className="h-3 w-3" />;
      case 'pending':
        return <Clock className="h-3 w-3" />;
      case 'error':
        return <AlertCircle className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getStatusVariant = () => {
    if (isSyncing) {
      return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    }
    switch (status) {
      case 'synced':
        return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      case 'pending':
        return 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800';
      case 'error':
        return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
      default:
        return '';
    }
  };

  const formatLastSynced = () => {
    try {
      return formatDistanceToNow(new Date(lastSyncedAt), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const getStatusText = () => {
    if (isSyncing) {
return 'Syncing...';
}
    switch (status) {
      case 'synced':
        return 'Synced';
      case 'pending':
        return 'Pending';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getStatusVariant()}`}
            >
              {getStatusIcon()}
              <span>{getStatusText()}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <div>Last synced: {formatLastSynced()}</div>
              {syncError && (
                <div className="text-red-500 mt-1">{syncError}</div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <span className="text-xs text-muted-foreground">
        {formatLastSynced()}
      </span>

      {onSync && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={onSync}
                disabled={isSyncing}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                <span className="sr-only">Sync now</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Sync from Encompass</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
